<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';

  import { getNotificationsContext } from '@client/components/notifications';
  import { SectionItem } from '@client/components/section';
  import { Select } from '@client/components/select';
  import { Spinner } from '@client/components/spinner';
  import { Step } from '@client/components/steps';

  import { VideoEditForm } from '@client/pages/manage/assets/edit/edit-video/shared';

  import { AssetsService } from '@client/services/assets';
  import { CampaignsService } from '@client/services/campaigns';

  import { appStore } from '@client/stores/app';

  import { secondsToHms } from '@common/utils/numbers';
  import { deepCopy } from '@common/utils/objects';

  /**
   * Controls whether the next button in the workflow step is disabled.
   * @type {boolean}
   * @default true
   */
  export let nextDisabled = true;

  /**
   * Callback function invoked when the workflow step executes.
   * @type {Function | null}
   * @default null
   */
  export let onExecute = null;

  /**
   * Callback function invoked when the workflow step is rolled back.
   * @type {Function | null}
   * @default null
   */
  export let onRollback = null;

  /**
   * Step configuration object containing step data and metadata.
   * @type {Object}
   * @default { data: {} }
   */
  export let step = { data: {} };

  const { addNotification } = getNotificationsContext();

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  let loading = true;
  let currentInternalStep = 0;
  let maxStepReached = 0;
  let videos = [];
  let selectedVideo = null;
  let video = null;
  let originalVideo = null;
  let invalidRulename = false;

  const loadVideos = async () => {
    const response = await CampaignsService.getVideoList();
    videos = response.map((vid) => ({
      label: vid.rulename,
      value: vid.rulename,
      ...vid
    }));
  };

  const onSelectVideo = (event) => {
    selectedVideo = event.detail;
  };

  const handleInternalNext = (stepIndex) => {
    if (stepIndex === 0 && selectedVideo) {
      originalVideo = deepCopy(selectedVideo);
      video = deepCopy(selectedVideo);
      currentInternalStep = 1;
      if (currentInternalStep > maxStepReached) {
        maxStepReached = currentInternalStep;
      }
    }
  };

  const handleInternalBack = (stepIndex) => {
    if (stepIndex > 0) {
      currentInternalStep = stepIndex - 1;
    }
  };

  const executeUpdate = async () => {
    $appStore.loading = true;

    try {
      const response = await AssetsService.updateVideo(video);

      if (response?.status >= 400) {
        notify($format('label.FAILURE.UPDATE', { values: { item: video.filename } }), 'error');
        throw new Error($format('label.FAILURE.UPDATE_VIDEO'));
      }

      notify($format('label.SUCCESS.UPDATE', { values: { item: video.filename } }));

      step.data = {
        id: video.rulename,
        rollbackData: {
          originalVideo
        }
      };

      return response;
    } catch (error) {
      notify($format('label.FAILURE.UPDATE', { values: { item: video.filename } }), 'error');
      throw error;
    } finally {
      $appStore.loading = false;
    }
  };

  const rollbackUpdate = async () => {
    if (!step.data?.rollbackData) {
      throw new Error($format('label.NO_ROLLBACK_DATA'));
    }

    $appStore.loading = true;

    try {
      const { originalVideo } = step.data.rollbackData;

      const response = await AssetsService.updateVideo(originalVideo);

      if (response?.status >= 400) {
        notify($format('label.FAILURE.ROLLBACK_VIDEO_UPDATE'), 'error');
        throw new Error($format('label.FAILURE.ROLLBACK_VIDEO_UPDATE'));
      }

      notify($format('label.SUCCESS.ROLLBACK_VIDEO_UPDATE'));

      return { success: true };
    } catch (error) {
      notify($format('label.FAILURE.ROLLBACK_VIDEO_UPDATE'), 'error');
      throw error;
    } finally {
      $appStore.loading = false;
    }
  };

  const hasChanges = () => {
    if (!originalVideo || !video) return false;
    return JSON.stringify(originalVideo) !== JSON.stringify(video);
  };

  onMount(async () => {
    loading = true;

    try {
      await loadVideos();
    } catch (error) {
      console.error('Failed to load videos:', error);
      notify($format('label.FAILURE.LOAD_VIDEOS'), 'error');
    } finally {
      loading = false;
    }

    if (onExecute) {
      onExecute(executeUpdate);
    }

    if (onRollback) {
      onRollback(rollbackUpdate);
    }
  });

  $: {
    if (currentInternalStep === 0) {
      nextDisabled = true; // Always disabled on step 0 (selection step)
    } else if (currentInternalStep === 1) {
      nextDisabled = !hasChanges() || !video?.rulename || invalidRulename;
    } else {
      nextDisabled = true;
    }
  }
</script>

<div class="video-update-form">
  <Step
    active={currentInternalStep === 0}
    disabled={false}
    title={$format('label.SELECT_VIDEO')}
    summary={selectedVideo?.filename || $format('label.NOT_SELECTED')}
    onNext={currentInternalStep === 0 ? () => handleInternalNext(0) : undefined}
    onStep={() => (currentInternalStep = 0)}
    nextDisabled={currentInternalStep === 0 && !selectedVideo}
  >
    {#if loading}
      <div class="loading">
        <Spinner show inline />
      </div>
    {:else}
      <SectionItem label={$format('label.VIDEO')} description={$format('label.CHOOSE_VIDEO_TO_UPDATE')}>
        <Select
          items={videos}
          bind:value={selectedVideo}
          placeholder={$format('label.PLACEHOLDER.SELECT_VIDEO')}
          clearable={false}
          showChevron
          on:change={onSelectVideo}
        >
          <svelte:fragment slot="item" let:item>
            <div class="item">
              <div class="item-detail">
                <div class="image">
                  <img
                    src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
                    style="background: url({item.thumbnail}) no-repeat center center;"
                    alt={item.title}
                    loading="lazy"
                  />
                </div>
                <div class="info">
                  <b>{item.rulename}</b>
                  <small>{$format('label.LENGTH')} {secondsToHms(item.duration)}</small>
                </div>
              </div>
            </div>
          </svelte:fragment>
        </Select>
      </SectionItem>
    {/if}
  </Step>

  <Step
    active={currentInternalStep === 1}
    disabled={maxStepReached < 1}
    title={$format('label.UPDATE_VIDEO')}
    summary={video?.rulename || $format('label.NOT_CONFIGURED')}
    onBack={currentInternalStep === 1 ? () => handleInternalBack(1) : undefined}
    onStep={() => {
      if (currentInternalStep >= 1) currentInternalStep = 1;
    }}
    nextDisabled={currentInternalStep === 1 && (!hasChanges() || !video?.rulename || invalidRulename)}
  >
    {#if video}
      <VideoEditForm bind:data={video} bind:invalidRulename />
    {/if}
  </Step>
</div>

<style lang="scss">
  .video-update-form {
    .loading {
      align-items: center;
      display: flex;
      justify-content: center;
      padding: var(--padding-xlarge);
    }

    :global(.section-item .row.align-items-center) {
      align-items: flex-start !important;
    }

    .item-detail {
      align-items: center;
      display: block;

      .info {
        align-items: start;
        display: flex;
        flex-direction: column;
        justify-content: center;

        b {
          color: var(--color-gray-900);
          text-align: center;
          text-wrap: wrap;
        }

        small {
          color: var(--color-gray-600);
          display: block;
          margin-top: var(--margin-xsmall);
        }
      }

      .image {
        align-items: start;
        display: flex;
        justify-content: start;
        margin-bottom: var(--margin-large);

        img {
          background-size: contain !important;
          border: 1px solid var(--border-color);
          border-radius: var(--border-radius-base);
          display: block;
          height: 120px;
          width: auto;
        }
      }
    }

    .placeholder {
      padding: var(--padding-xlarge);
      text-align: center;
      color: var(--color-gray-600);

      p {
        margin-bottom: var(--margin-small);

        &:last-child {
          margin-bottom: 0;
        }
      }

      .text-muted {
        font-size: var(--font-size-small);
      }
    }
  }
</style>
